import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    createStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    fetchByIdStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed',
    updateStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    deleteStatus: "idle", // 'idle' | 'loading' | 'success' | 'failed'
    pagination: {},
    error: null
};


export const fetchAllTransactionType = createAsyncThunk('transactionType/fetchAllTransactionType', async ({
                                                                                        scope,
                                                                                        scopeId,
                                                                                        roleSign,
                                                                                        page,
                                                                                        limit,
                                                                                    } = {}) => {
    try {
        let response;
        const params = new URLSearchParams();
        if (page) params.append("page", page);
        if (limit) params.append("limit", limit);

        const queryString = params.toString();
        const fullParams = queryString ? `${queryString}` : '';
        response = await api.get(`/${roleSign}/transactionType?${fullParams}`);

        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const createTransacationType = createAsyncThunk('transactionType/createTransacationType', async ({roleSign, scopeId, data}) => {
    try {
        const response = await api.post(`${roleSign}/transactionType`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const updateTransactionType = createAsyncThunk('transactionType/updateTransactionType', async ({
                                                                                          scope,
                                                                                          roleSign,
                                                                                          data,
                                                                                          id
                                                                                      }) => {
    try {
        let response = await api.patch(`/${roleSign}/transactionType/${id}`, data);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const fetchTransactionTypeById = createAsyncThunk('transactionType/fetchTransactionTypeById', async ({scope, roleSign, id}) => {
    try {
        let response = await api.get(`/${roleSign}/transactionType/${id}`);
        return {...response};
    } catch (error) {
        if (error.response) return error.response;
        throw error;
    }
});

export const deleteTransactionType = createAsyncThunk('transactionType/deleteTransactionType',
    async ({roleSign, id}) => {
        try {
            const response = await api.delete(`/${roleSign}/transactionType/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

const transactionTypeSlice = createSlice({
    name: "transactionType",
    initialState,
    reducers: {
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder

            //create Transaction type
            .addCase(createTransacationType.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createTransacationType.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createTransacationType.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch all transaction type
            .addCase(fetchAllTransactionType.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchAllTransactionType.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchAllTransactionType.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch Transaction type by ID
            .addCase(fetchTransactionTypeById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchTransactionTypeById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchTransactionTypeById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Update Transaction type
            .addCase(updateTransactionType.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateTransactionType.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateTransactionType.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

        //Delete Transaction type
            .addCase(deleteTransactionType.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteTransactionType.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg.id;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteTransactionType.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetCreateStatus,
    resetUpdateStatus,
    resetDeleteStatus,
} = transactionTypeSlice.actions;
export default transactionTypeSlice.reducer;
